import type { HTMLAttributes, Ref } from 'react';
import { useEffect, useState } from 'react';
import type { PrimitiveProps } from './Button';
import { cx } from './cx';
import s from './Skeleton.module.css';

export type SkeletonVariant = 'line' | 'block' | 'row';

export interface SkeletonProps extends PrimitiveProps<HTMLAttributes<HTMLDivElement>> {
  variant?: SkeletonVariant;
  /** How many shapes to stack. For `line`, the last one is drawn short. */
  lines?: number;
  /**
   * Announced while the region loads. One skeleton per region passes it and the rest stay
   * silent: a screen that says "Loading" a dozen times has told the reader nothing.
   */
  label?: string;
  ref?: Ref<HTMLDivElement>;
}

const VARIANT: Record<SkeletonVariant, string | undefined> = {
  line: s.line,
  block: s.block,
  row: s.row,
};

/**
 * Mounted empty and given its words after mount: a status region that arrives in the same
 * paint as its first text is not reliably announced — the reason CopyButton's and Toast's
 * live regions are mounted empty too. A skeleton is mounted fresh with each load, so the
 * region cannot simply be kept around the way theirs are.
 */
function LoadingStatus({ label }: { label: string }) {
  const [text, setText] = useState('');
  useEffect(() => {
    setText(label);
  }, [label]);
  return (
    <span role="status" className={s.srOnly}>
      {text}
    </span>
  );
}

export function Skeleton({ variant = 'line', lines = 1, label, ...rest }: SkeletonProps) {
  const count = Math.max(1, Math.floor(lines));

  return (
    <div {...rest} className={s.skeleton}>
      {label ? <LoadingStatus label={label} /> : null}
      {Array.from({ length: count }, (_, i) => (
        <span
          key={i}
          aria-hidden="true"
          className={cx(
            s.shape,
            VARIANT[variant],
            variant === 'line' && count > 1 && i === count - 1 && s.short,
          )}
        />
      ))}
    </div>
  );
}

export default Skeleton;
